import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { X } from "lucide-react";

const AuthModal = ({ open, mode = "login", onClose, onSwitch }) => {
  const { login, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        if (!form.name) {
          setError("Ingresa tu nombre");
          setLoading(false);
          return;
        }
        await register(form.name, form.email, form.password);
      }
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Error de autenticación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md p-8 md:p-10 relative"
      >
        <button
          onClick={onClose}
          data-testid="auth-close-btn"
          className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-900"
        >
          <X className="w-5 h-5" />
        </button>

        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#4F46E5] mb-3">
          {mode === "login" ? "Bienvenido de vuelta" : "Únete a Kursos Online"}
        </p>
        <h2 className="font-display font-black text-3xl tracking-tighter mb-6">
          {mode === "login" ? "Ingresa a tu cuenta" : "Crea tu cuenta gratis"}
        </h2>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="text-xs uppercase tracking-wider font-bold mb-2 block">
                Nombre
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-zinc-300 px-4 py-3 focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] outline-none"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-2 block">Correo</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-zinc-300 px-4 py-3 focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] outline-none"
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-2 block">
              Contraseña
            </label>
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full border border-zinc-300 px-4 py-3 focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] outline-none"
              data-testid="auth-password-input"
            />
          </div>

          {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-btn"
            className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white py-3.5 font-semibold transition disabled:opacity-50"
          >
            {loading ? "Procesando..." : mode === "login" ? "Ingresar" : "Crear cuenta"}
          </button>
        </form>

        <p className="mt-6 text-sm text-center text-zinc-600">
          {mode === "login" ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}{" "}
          <button
            onClick={() => onSwitch(mode === "login" ? "register" : "login")}
            data-testid="auth-switch-btn"
            className="text-[#4F46E5] font-semibold hover:underline"
          >
            {mode === "login" ? "Regístrate" : "Ingresa"}
          </button>
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
